import * as React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Stack,
  TextField,
  LinearProgress,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { FormattedMessage, injectIntl } from 'react-intl';

import MoodIcon from '@mui/icons-material/Mood';
import TagFacesTwoToneIcon from '@mui/icons-material/TagFacesTwoTone';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import MoodBadIcon from '@mui/icons-material/MoodBad';

import FullscreenMessage from './FullscreenMessage';

class ShowSurvey extends React.Component {
  // translate only these keys (everything else stays raw backend text)
  OFFICIAL_ANSWERS = new Set(['excellent', 'very_good', 'good', 'average', 'bad', 'poor', 'very_poor']);

  // the satisfaction set that should show icons
  SATISFACTION_SET = new Set(['excellent', 'very_good', 'average', 'poor', 'very_poor']);

  state = {
    window: '', // '', 'fullscreen_message', 'analyze_data'
    info_message: '',

    question: '',
    ans: [],

    selected_btn: 0,

    cust_name: '',
    comment: '',
    show_comment: false,

    data: [],

    isLoading: true,
    isSubmitting: false,
    error: '',
    hasVoted: false,
  };

  componentDidMount() {
    const hasVoted = localStorage.getItem(this.getVoteKey()) === 'done';
    this.setState({ hasVoted });

    this.loadSurvey().then(() => {
      if (hasVoted) this.showData();
    });
  }

  // ---------- helpers ----------
  getVoteKey() {
    return `etv_survey_${this.props.survey_id}`;
  }

  getNavigatorLanguage() {
    if (navigator.languages && navigator.languages.length) return navigator.languages[0];
    return navigator.userLanguage || navigator.language || navigator.browserLanguage || 'en';
  }

  translateDate(d) {
    const date = new Date(d);
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return date.toLocaleString(this.getNavigatorLanguage(), options);
  }

  trans(msgID) {
    return <FormattedMessage id={msgID} defaultMessage={`[${msgID}]`} />;
  }

  normalizeAnswerKey(s) {
    if (!s) return '';
    return String(s).trim().toLowerCase().replace(/\s+/g, '_');
  }

  // Translate ONLY official answers; otherwise show raw backend text
  tAnswer(label) {
    if (!label) return label;

    const key = this.normalizeAnswerKey(label);
    if (!this.OFFICIAL_ANSWERS.has(key)) return label;

    const { intl } = this.props;
    if (!intl) return label;

    return intl.formatMessage({ id: key, defaultMessage: label });
  }

  // Satisfaction survey detection: must contain the 5 satisfaction keys (order independent)
  isSatisfactionSurvey() {
    const keys = this.state.ans.map((x) => this.normalizeAnswerKey(x));
    return ['excellent', 'good', 'average', 'poor', 'very_poor'].every((k) => keys.includes(k));
  }

  iconForAnswer(label) {
    const key = this.normalizeAnswerKey(label);

    switch (key) {
      case 'excellent':
        return <MoodIcon sx={{ fontSize: 34, color: '#2e7d32' }} />; // dark green
      case 'very_good':
      case 'good': // <-- FIX: treat "good" as same as very_good icon if it appears
        return <TagFacesTwoToneIcon sx={{ fontSize: 34, color: '#43a047' }} />; // green
      case 'average':
        return <SentimentSatisfiedIcon sx={{ fontSize: 34, color: '#fbc02d' }} />; // yellow
      case 'poor':
        return <SentimentDissatisfiedIcon sx={{ fontSize: 34, color: '#fb8c00' }} />; // orange
      case 'very_poor':
        return <MoodBadIcon sx={{ fontSize: 34, color: '#d32f2f' }} />; // red
      default:
        return null;
    }
  }

  // label renderer:
  // - general surveys: NO icon
  // - satisfaction surveys: icon ONLY for the satisfaction keys (not for any random answers)
  renderAnswerLabel(label, isSatisfactionSurvey) {
    const key = this.normalizeAnswerKey(label);

    if (!isSatisfactionSurvey) {
      return this.tAnswer(label);
    }

    const showIcon = this.SATISFACTION_SET.has(key) || key === 'good' || key === 'very_good';
    if (!showIcon) {
      return this.tAnswer(label);
    }

    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>{this.iconForAnswer(label)}</Box>
        <span>{this.tAnswer(label)}</span>
      </Stack>
    );
  }

  extractSurveyData(answersHits) {
    const data = [];
    data[0] = ['Answer', 'Hits'];
    for (let i = 0; i < answersHits.length; i++) {
      data[i + 1] = [answersHits[i].answer, answersHits[i].hits];
    }
    return data;
  }

  // ---------- data ----------
  async loadSurvey() {
    try {
      const surveyID = this.props.survey_id;
      const url = `https://elevatortvbackend.appspot.com/survey_mgmt?action=survey_info&survey_id=${surveyID}`;
      const res = await fetch(url);
      const result = await res.json();

      if (result?.result === false) {
        this.setState({ isLoading: false });
        this.showFullScreenMessage(result?.reason || 'error');
        return;
      }

      const answers = result?.ans || [];
      const ans = answers.map((a) => a.answer);

      this.setState({
        question: result?.question || '',
        ans,
        data: this.extractSurveyData(answers),
        isLoading: false,
        error: '',
      });
    } catch (e) {
      this.setState({ isLoading: false, error: 'load_error' });
    }
  }

  async reportHit() {
    if (this.state.hasVoted) {
      this.showData();
      return;
    }

    const { selected_btn, comment, cust_name } = this.state;
    if (selected_btn < 1) return;

    try {
      this.setState({ isSubmitting: true, error: '' });

      const surveyID = this.props.survey_id;
      let url =
        `https://elevatortvbackend.appspot.com/survey_mgmt?action=report_hit` +
        `&survey_id=${surveyID}` +
        `&answer_id=${selected_btn}` +
        `&location_id=${this.props.location_id}`;

      if ((comment || '').trim().length > 0 || (cust_name || '').trim().length > 0) {
        url += `&comment=${encodeURIComponent((comment || '').trim())}&cust_name=${encodeURIComponent(
          (cust_name || '').trim()
        )}`;
      }

      const res = await fetch(url);
      const result = await res.json();

      if (result?.result === false) {
        this.setState({ isSubmitting: false });
        this.showFullScreenMessage(result?.reason || 'error');
        return;
      }

      localStorage.setItem(this.getVoteKey(), 'done');

      const updated = this.state.data.map((row, idx) => {
        if (idx === 0) return row;
        if (idx === selected_btn) return [row[0], (row[1] || 0) + 1];
        return row;
      });

      this.setState({
        data: updated,
        isSubmitting: false,
        hasVoted: true,
        cust_name: '',
        comment: '',
        show_comment: false,
      });

      this.showData();
    } catch (e) {
      this.setState({ isSubmitting: false, error: 'submit_error' });
    }
  }

  // ---------- ui state ----------
  showFullScreenMessage(message) {
    this.setState({ info_message: message, window: 'fullscreen_message' });
  }

  showData() {
    this.setState({ window: 'analyze_data' });
  }

  // ---------- chart helpers ----------
  getTotalHits() {
    return this.state.data.slice(1).reduce((sum, row) => sum + (row[1] || 0), 0);
  }

  getPercent(hits) {
    const total = this.getTotalHits();
    if (!total) return 0;
    return Math.round((hits / total) * 100);
  }

  // ---------- renders ----------
  renderLoading() {
    return (
      <Box sx={{ minHeight: '100svh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Card sx={{ width: '100%', maxWidth: 520, borderRadius: 3 }}>
          <CardContent>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <CircularProgress />
              <Typography variant="body1" color="text.secondary">
                {this.trans('loading')}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  renderAnalyze() {
    const rows = this.state.data.slice(1);
    const isSatisfaction = this.isSatisfactionSurvey();

    return (
      <Box sx={{ minHeight: '100svh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Card sx={{ width: '100%', maxWidth: 560, borderRadius: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {this.state.question}
              </Typography>

              <Divider />

              <Stack spacing={2}>
                {rows.map((row, i) => {
                  const label = row[0];
                  const hits = row[1] || 0;
                  const pct = this.getPercent(hits);

                  return (
                    <Box key={`${label}-${i}`} sx={{ px: 0.5 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {this.renderAnswerLabel(label, isSatisfaction)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {hits} • {pct}%
                        </Typography>
                      </Stack>
                      <LinearProgress variant="determinate" value={pct} />
                    </Box>
                  );
                })}
              </Stack>

              <Divider sx={{ my: 1 }} />

              <Typography variant="caption" color="text.secondary">
                {this.trans('updated')} {this.translateDate(new Date())}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  renderQuestion() {
    if (this.state.hasVoted) return this.renderAnalyze();

    const isSatisfaction = this.isSatisfactionSurvey();

    const AnswerButton = ({ idx, label }) => {
      const selected = this.state.selected_btn === idx;

      return (
        <Button
          onClick={() => this.setState({ selected_btn: idx })}
          variant={selected ? 'contained' : 'outlined'}
          color={selected ? 'primary' : 'inherit'}
          size="large"
          fullWidth
          sx={{ justifyContent: 'flex-start', borderRadius: 2, py: 1.5, px: 2 }}
          aria-pressed={selected}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {this.renderAnswerLabel(label, isSatisfaction)}
          </Typography>
        </Button>
      );
    };

    return (
      <Box sx={{ minHeight: '100svh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Card sx={{ width: '100%', maxWidth: 560, borderRadius: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              {!!this.state.error && <Alert severity="error">{this.trans(this.state.error)}</Alert>}

              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {this.state.question}
              </Typography>

              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {this.state.ans.map((label, i) => (
                  <AnswerButton key={i} idx={i + 1} label={label} />
                ))}
              </Stack>

              <Stack spacing={1.25} sx={{ mt: 2 }}>
                <TextField
                  value={this.state.cust_name}
                  onChange={(e) => this.setState({ cust_name: e.target.value })}
                  label={this.trans('name_optional')}
                  variant="outlined"
                  fullWidth
                  size="medium"
                  inputProps={{ inputMode: 'text' }}
                />

                {!this.state.show_comment ? (
                  <Button
                    variant="outlined"
                    onClick={() => this.setState({ show_comment: true })}
                    sx={{ borderRadius: 2, py: 1.1 }}
                  >
                    {this.trans('add_comment_optional')}
                  </Button>
                ) : (
                  <TextField
                    value={this.state.comment}
                    onChange={(e) => this.setState({ comment: e.target.value })}
                    label={this.trans('comment_optional')}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={3}
                  />
                )}
              </Stack>
            </Stack>
          </CardContent>

          <CardActions sx={{ justifyContent: 'center', px: 2, pb: 2 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              disabled={this.state.selected_btn < 1 || this.state.isSubmitting}
              onClick={() => this.reportHit()}
              sx={{ borderRadius: 2, py: 1.25 }}
            >
              {this.state.isSubmitting ? (
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                  <CircularProgress size={20} />
                  <Typography variant="h6">{this.trans('submitting')}</Typography>
                </Stack>
              ) : (
                <Typography variant="h6">{this.trans('submit')}</Typography>
              )}
            </Button>
          </CardActions>
        </Card>
      </Box>
    );
  }

  render() {
    if (this.state.window === 'fullscreen_message') {
      return <FullscreenMessage message={this.state.info_message} />;
    }
    if (this.state.isLoading) return this.renderLoading();
    if (this.state.window === 'analyze_data') return this.renderAnalyze();
    return this.renderQuestion();
  }
}

export default injectIntl(ShowSurvey);
